/**
 * Shared infrastructure for the edit-mode store slice:
 * state/object types, the `applyResult` pipeline, the generator-bake
 * cascade, the `runEditOp` higher-order helper, the raycast bridge, and
 * the selection-connected flood fill.
 *
 * All op sub-modules (`selectionOps`, `meshOps`, `curveOps`) import
 * from here. This is the only file in the slice that touches
 * `undoManager` / `captureSnapshot` / `replaceSceneObject*` directly —
 * everything else routes through `runEditOp`.
 */

import * as THREE from "three/webgpu";
import { dlog } from "../../debug/debugLogger.ts";
import {
  clearEditSelection,
  vertexCentroid,
} from "../../modeling/editSelection.ts";
import {
  applyFaceShadingOverride,
  recomputeLoopNormals as meshRecomputeLoopNormals,
  resolveAutoSmoothAngle,
  type VertexProvenance,
} from "../../modeling/meshOps.ts";
import {
  validateMeshCoherence,
  validateTopology,
} from "../../modeling/ops/validateTopology.ts";
import {
  deriveFaceProvenance,
  inheritTopologyAttributes,
  remapFaceAttributes,
  remapPerVertexFloat,
  remapPerVertexFloat3,
  remapVertexGroups,
} from "../../modeling/provenance.ts";
import { validateSceneObject } from "../../modeling/sceneObjectValidation.ts";
import {
  getAdjacencyCache,
  topologyFaceCount,
  triangulateTopologyForRendering,
  vertexNeighbors,
} from "../../modeling/topology.ts";
import type {
  LoopNormals,
  PolygonTopology,
  RenderTriangleIndices,
  SceneObject,
} from "../../modeling/types.ts";
import { patchWithUVs } from "../../modeling/uvUnwrap.ts";
import { pruneSceneSketch } from "../../sketch/sceneSketch.ts";
import { worldTransformForSceneObject } from "../collectionGraph.ts";
import type { SceneObjectMap } from "../sceneObjectMap.ts";
import { patchSceneObject, replaceSceneObject } from "../sceneObjectMap.ts";
import type { AppState, Collection } from "../storeTypes.ts";
import { captureSnapshot, undoManager } from "../storeUndo.ts";

// ---------------------------------------------------------------------------
// Store-slice primitives
// ---------------------------------------------------------------------------

export type Set_ = (partial: Partial<AppState>) => void;
export type Get_ = () => AppState;

/** Log operator entry with selection context. */
export function traceEditOp(
  op: string,
  em: {
    selectedVertices: Set<number>;
    selectedFaces: Set<number>;
    granularity: string;
  },
): void {
  dlog.trace(
    "js",
    `edit.${op}`,
    `sel: ${em.selectedVertices.size}v ${em.selectedFaces.size}f mode:${em.granularity}`,
    {
      op,
      selVerts: em.selectedVertices.size,
      selFaces: em.selectedFaces.size,
      granularity: em.granularity,
    },
  );
}

// ---------------------------------------------------------------------------
// Normal recomputation + per-vertex attribute reconciliation
// ---------------------------------------------------------------------------

/**
 * Compute normals for the result of a geometry operation using the source
 * object's auto-smooth settings. Returns a geometry patch with normals
 * and any vertex splits required by sharp edges.
 *
 * Typically called via `applyResult` which also runs dev-mode validation.
 */
export function computeNormalsForObject(
  obj: SceneObject,
  result: {
    vertices?: Float32Array;
    indices?: RenderTriangleIndices;
    topology?: PolygonTopology;
    faceAttributes?: SceneObject["faceAttributes"];
  },
): { loopNormals: LoopNormals } {
  const verts = result.vertices ?? obj.vertices;
  const topo = result.topology ?? obj.topology;
  // Note: per-face shading reads from the *result* face attributes when the
  // op already reconciled them, otherwise the object's. `applyResult`
  // recomputes normals on the post-reconciliation object, so this defaulting
  // is mostly a guard for direct callers.
  const faceAttrs = result.faceAttributes ?? obj.faceAttributes;
  const angle = resolveAutoSmoothAngle(obj.shading, obj.autoSmoothAngle);
  const loopNormals = meshRecomputeLoopNormals(verts, topo, angle);
  applyFaceShadingOverride(loopNormals, verts, topo, faceAttrs);
  return { loopNormals };
}

/**
 * Reconcile every per-vertex attribute on a `SceneObject` with a topology-op
 * result via the op's `VertexProvenance`.
 *
 * `provenance.parents` answers "for each NEW vert, which OLD verts contributed
 * to it" — average parent values for floats, union parent memberships for
 * groups. `provenance.remap` answers "for each OLD vert, where did it go" —
 * unused here directly because reconciliation is more naturally written in
 * the new-vert direction, but exposed by the same helper to consumers that
 * want to forward-project (e.g. selection updates).
 *
 * Adding a new per-vertex attribute is a one-line extension here. Every
 * attribute uses the same primitive (`remapPerVertexFloat3`,
 * `remapPerVertexFloat`, `remapVertexGroups`) so behaviour is uniform.
 */
function reconcilePerVertexAttributes(
  out: SceneObject,
  newVertices: Float32Array,
  provenance: VertexProvenance,
): SceneObject {
  const newVc = newVertices.length / 3;
  let next = out;

  if (next.vertexGroups) {
    next = {
      ...next,
      vertexGroups: remapVertexGroups(next.vertexGroups, provenance),
    };
  }

  if (next.skinRadii) {
    next = {
      ...next,
      skinRadii: remapPerVertexFloat(next.skinRadii, provenance, newVc, 1),
    };
  }

  if (next.baseVertices) {
    next = {
      ...next,
      // For a new vert with no parents, default to its own current position
      // (the mesh's `newVertices` array) so the sculpt displacement stays
      // anchored to the bake.
      baseVertices: remapPerVertexFloat3(
        next.baseVertices,
        provenance,
        newVc,
        newVertices,
      ),
    };
  }

  return next;
}

/**
 * Reconcile every per-face attribute on a `SceneObject` with a topology-op
 * result via a derived `FaceProvenance`.
 *
 * The face provenance is built from the op's vertex provenance plus the
 * old/new topologies — see `deriveFaceProvenance` for the matching rule.
 * This avoids requiring every WASM op to emit dedicated face provenance
 * up-front; once individual ops gain Rust-side face tracking, swap in the
 * direct provenance and skip the heuristic.
 *
 * Adding a new per-face attribute is a one-line extension to
 * `remapFaceAttributes` — every field uses the same primitive
 * (`remapPerFaceUint16`, `remapPerFaceUint8`, `remapFaceGroups`) so behaviour
 * is uniform.
 */
function reconcilePerFaceAttributes(
  out: SceneObject,
  oldTopology: PolygonTopology,
  newTopology: PolygonTopology,
  provenance: VertexProvenance,
): SceneObject {
  if (!out.faceAttributes) return out;
  const newFaceCount = topologyFaceCount(newTopology);
  if (newFaceCount === 0) {
    const { faceAttributes: _fa, ...rest } = out;
    return rest as SceneObject;
  }
  const faceProv = deriveFaceProvenance(oldTopology, newTopology, provenance);
  const remapped = remapFaceAttributes(
    out.faceAttributes,
    faceProv,
    newFaceCount,
  );
  if (!remapped) {
    const { faceAttributes: _fa, ...rest } = out;
    return rest as SceneObject;
  }
  return { ...out, faceAttributes: remapped };
}

/**
 * Apply a geometry operation result to a scene object: reconcile every
 * per-vertex attribute through the op's `VertexProvenance`, recompute
 * normals, regenerate UVs, and (in dev builds) validate the final topology.
 *
 * `provenance` is required — every op that reaches `applyResult` MUST
 * produce one (WASM ops via `unpack`, JS-side ops via `identityProvenance`
 * or a hand-built `VertexProvenance`). There is no fallback path; this
 * keeps the per-vertex-attribute reconciliation uniform with no special
 * cases for "ops that didn't bother to track remapping".
 */
export function applyResult(
  obj: SceneObject,
  result: {
    vertices: Float32Array;
    topology: PolygonTopology;
    provenance: VertexProvenance;
  },
  label: string,
): SceneObject {
  const t0 = performance.now();
  const topo = result.topology;
  const prevVerts = obj.vertices.length / 3;
  const prevFaces = topologyFaceCount(obj.topology);
  // Always derive corner-based render indices from topology — this is the
  // single source of truth. Operations should not produce their own indices.
  const indices = triangulateTopologyForRendering(topo);
  // Reconcile per-face attributes on a temporary so the normal recompute
  // below can apply the post-op `faceAttributes.shading` override (per-face
  // shading must reflect the new face indices, not the pre-op ones).
  const reconciledFaceAttrs = obj.faceAttributes
    ? reconcilePerFaceAttributes(
        obj,
        obj.topology,
        result.topology,
        result.provenance,
      ).faceAttributes
    : undefined;
  const normals = computeNormalsForObject(obj, {
    ...result,
    faceAttributes: reconciledFaceAttrs,
  });
  const patched = {
    vertices: result.vertices,
    topology: result.topology,
    indices,
    ...normals,
  };
  if (import.meta.env.DEV) {
    const verts = patched.vertices;
    validateTopology(patched.topology, verts.length / 3, label);
    // Skip UV validation here — patchWithUVs() regenerates them next.
    validateMeshCoherence(verts, topo, normals.loopNormals, undefined, label);
  }
  let out = patchWithUVs(obj, patched);
  // Topology mutated — bump the version so projected sketch points whose
  // stamped attachment is older than this one (and that carry `corners` data)
  // get dropped on the next render.
  out = { ...out, topologyVersion: (obj.topologyVersion ?? 0) + 1 };
  out = reconcilePerVertexAttributes(out, result.vertices, result.provenance);
  // Apply the already-computed reconciled face attributes (avoids running
  // `deriveFaceProvenance` a second time).
  if (obj.faceAttributes) {
    if (reconciledFaceAttrs) {
      out = { ...out, faceAttributes: reconciledFaceAttrs };
    } else {
      const { faceAttributes: _fa, ...rest } = out;
      out = rest as SceneObject;
    }
  }
  out = {
    ...out,
    topology: inheritTopologyAttributes(
      obj.topology,
      out.topology,
      result.provenance,
    ),
  };
  // Destructive topology op → bake this object's generator ref away. The
  // single-object clear runs here; if the object is a group child, the
  // caller cascades the bake to the rest of the group (see
  // `replaceSceneObjectAfterEdit`).
  if (out.generator) {
    const { generator: _gen, ...rest } = out;
    out = rest as SceneObject;
  }
  if (import.meta.env.DEV) {
    const invalid = validateSceneObject(out);
    if (invalid) {
      throw new Error(`[${label}] ${invalid.reason}`);
    }
  }
  const newVerts = patched.vertices.length / 3;
  const newFaces = topologyFaceCount(topo);
  const ms = performance.now() - t0;
  dlog.info(
    "js",
    `edit.${label}`,
    `${prevVerts}v/${prevFaces}f -> ${newVerts}v/${newFaces}f (${ms.toFixed(1)}ms)`,
    {
      op: label,
      prevVerts,
      prevFaces,
      newVerts,
      newFaces,
      durationMs: +ms.toFixed(1),
    },
  );
  return out;
}

// ---------------------------------------------------------------------------
// Generator bake — cleared on the first destructive edit
// ---------------------------------------------------------------------------

/**
 * State slice the bake helpers read from. We pass the full maps in (not
 * `AppState`) so these helpers stay callable from any slice without a
 * circular type import.
 */
interface BakeState {
  sceneObjects: SceneObjectMap;
  collections: Map<string, Collection>;
}

/**
 * Patch returned by the bake helpers. Callers spread this into `set()`.
 * `collections` is only present when a collection-owned generator was
 * actually baked — avoiding a no-op overwrite of an unchanged map.
 */
export interface EditStatePatch {
  sceneObjects: SceneObjectMap;
  collections?: Map<string, Collection>;
}

/**
 * Strip any generator ownership that covers `id` — both the single-mesh
 * ref on the object itself and the compound ref on any collection that
 * holds it as a member. Returns the new sceneObjects map plus an
 * optional collections map (present only when a collection was baked).
 *
 * An object is a member of at most one generator-backed collection by
 * construction (compound generators always create a fresh collection),
 * so we stop at the first match.
 */
function cascadeGeneratorBake(state: BakeState, id: string): EditStatePatch {
  let sceneObjects = state.sceneObjects;
  let collections: Map<string, Collection> | undefined;

  // Single-mesh: strip the ref on the object itself.
  const obj = sceneObjects.get(id);
  if (obj?.generator) {
    const { generator: _gen, ...rest } = obj;
    sceneObjects = replaceSceneObject(sceneObjects, id, rest as SceneObject);
  }

  // Compound: find a generator-backed collection containing this object
  // and clear its ref. Its siblings remain members, but the collection
  // is now a plain, fully-baked group.
  for (const [collId, coll] of state.collections) {
    if (!coll.generatorRef) continue;
    if (!coll.objectIds.has(id)) continue;
    const { generatorRef: _gr, ...rest } = coll;
    collections = new Map(state.collections);
    collections.set(collId, rest);
    break;
  }

  return collections ? { sceneObjects, collections } : { sceneObjects };
}

/**
 * Partial-patch wrapper around `patchSceneObject` that bakes the generator
 * on the target (and any generator-backed collection it belongs to) as
 * part of the patch.
 *
 * Use for edit-mode operations that modify geometry via a partial patch —
 * vertex transforms committed through the command buffer, etc. The
 * destructive nature of those edits invalidates the generator's ability
 * to reproduce the mesh, so the ref is cleared atomically with the patch.
 *
 * Returns an `EditStatePatch` that callers spread into `set()`. Only
 * includes `collections` when a collection bake actually occurred.
 */
export function patchSceneObjectAfterEdit(
  state: BakeState,
  id: string,
  patch: Partial<SceneObject>,
): EditStatePatch {
  const baked = cascadeGeneratorBake(state, id);
  return {
    sceneObjects: patchSceneObject(baked.sceneObjects, id, patch),
    ...(baked.collections ? { collections: baked.collections } : {}),
  };
}

/**
 * Replace a scene object after a destructive edit op, baking any
 * generator-backed collection it belongs to at the same time.
 *
 * Policy: editing one wall of a Room bakes the *entire* collection
 * atomically. Letting one child drift while its siblings remain
 * regenerable would introduce a two-phase state where the next rebuild
 * would silently lose the user's edits.
 *
 * `applyResult` already strips the single-object `generator` ref on the
 * patched object, so this helper only needs to propagate the bake to
 * the owning collection.
 */
export function replaceSceneObjectAfterEdit(
  state: BakeState,
  id: string,
  patched: SceneObject,
): EditStatePatch {
  const baked = cascadeGeneratorBake(state, id);
  return {
    sceneObjects: replaceSceneObject(baked.sceneObjects, id, patched),
    ...(baked.collections ? { collections: baked.collections } : {}),
  };
}

// ---------------------------------------------------------------------------
// runEditOp — the single "edit mode topology mutation" ceremony
// ---------------------------------------------------------------------------

/**
 * Minimum shape every topology-mutating op must return to flow through
 * `runEditOp` / `applyResult`. Extra fields (`newSelection`,
 * `newSelectedFaces`, cleanup counts, …) are returned as-is by the
 * generic.
 */
export interface EditOpResultCore {
  vertices: Float32Array;
  topology: PolygonTopology;
  provenance: VertexProvenance;
}

/**
 * End-to-end driver for every topology-mutating edit operation in the
 * store slice.
 *
 * Handles the ceremony that used to be copy-pasted at 25+ call sites:
 *
 *   1. Guard — edit mode active, object exists
 *   2. Trace the operation with current selection context
 *   3. Run the mesh op (bail on null / empty result)
 *   4. Push an undo snapshot
 *   5. Apply the op result with `applyResult`
 *   6. Install the new editMode (default: {@link clearEditSelection})
 *   7. Commit the patched scene objects + canUndo/canRedo via `set`
 *
 * The default post-op editMode has every selection and hidden set
 * cleared — CLAUDE.md requires this because the op has invalidated
 * vertex/face indices. Operations that want to preserve a fresh
 * selection (extrude, merge, bridgeEdgeLoops, …) pass
 * `opts.selectionAfter`; it receives the ORIGINAL pre-op editMode,
 * the post-cleared base, and the raw op result, and returns the
 * editMode to install. Ops that truly don't touch topology (flip
 * normals, recalculate outside) can pass `{ preserveSelection: true }`
 * instead to keep the original editMode untouched.
 *
 * Returns the raw op result on success (for callers that want to
 * inspect side-data like merge-by-distance counts) or `null` on any
 * guard failure or op bailout.
 */
export function runEditOp<R extends EditOpResultCore>(
  get: Get_,
  set: Set_,
  label: string,
  run: (
    obj: SceneObject,
    em: import("../../modeling/types.ts").EditModeState,
  ) => R | null,
  opts?:
    | {
        /** Preserve the caller's original editMode untouched. */
        preserveSelection: true;
      }
    | {
        /**
         * Override the default cleared post-op editMode. Receives the
         * original pre-op editMode, the cleared base, and the raw
         * result, and returns the editMode to install.
         */
        selectionAfter: (
          original: import("../../modeling/types.ts").EditModeState,
          cleared: import("../../modeling/types.ts").EditModeState,
          result: R,
        ) => import("../../modeling/types.ts").EditModeState;
      },
): R | null {
  const em = get().editMode;
  if (!em) return null;
  traceEditOp(label, em);
  const obj = get().sceneObjects.get(em.objectId);
  if (!obj) return null;

  const result = run(obj, em);
  if (!result) return null;

  undoManager.push(captureSnapshot(get()));
  const patched = applyResult(obj, result, label);
  const clearedEm = clearEditSelection(em);
  const nextEm =
    opts && "preserveSelection" in opts
      ? em
      : opts && "selectionAfter" in opts
        ? opts.selectionAfter(em, clearedEm, result)
        : clearedEm;

  // Drop projected sketch strokes whose corners reference now-stale topology.
  // The renderer also drops them lazily, but updating scene sketch here keeps
  // the persisted state and undo snapshot in sync.
  const sketch = pruneSceneSketch(get().sketch, {
    topologyBumped: new Map([[em.objectId, patched.topologyVersion ?? 0]]),
  });

  set({
    ...replaceSceneObjectAfterEdit(get(), em.objectId, patched),
    editMode: nextEm,
    canUndo: undoManager.canUndo,
    canRedo: undoManager.canRedo,
    sketch,
  });

  return result;
}

// ---------------------------------------------------------------------------
// Rip operator projection helper
// ---------------------------------------------------------------------------

/**
 * Project a screen-space mouse point into the object's local coordinate
 * frame — used by the rip operator to decide which side of the selected
 * vertex fan the user wants to tear off.
 *
 * Computes the selection centroid in local space, projects it to NDC
 * using the active camera's full world matrix, and then unprojects the
 * mouse at the same NDC depth back into local space. Falls back to the
 * centroid itself (→ splits the fan in half) if the viewport camera
 * globals are not yet populated (e.g. mesh ops running before first
 * render, unit tests, etc).
 */
export function ripTargetPoint(
  obj: SceneObject,
  parentCollection: Collection | null,
  selectedVertices: Set<number>,
  mouseX: number,
  mouseY: number,
): [number, number, number] {
  const camera = (window as unknown as { __poseref_camera?: THREE.Camera })
    .__poseref_camera;
  const getRect = (
    window as unknown as { __poseref_canvasRect?: () => DOMRect }
  ).__poseref_canvasRect;

  const centroid = vertexCentroid(obj.vertices, selectedVertices);

  if (!camera || !getRect || !centroid) {
    // Fallback: selection centroid (or origin if empty) in local space.
    return centroid ?? [0, 0, 0];
  }

  const rect = getRect();
  const ndcX = ((mouseX - rect.left) / rect.width) * 2 - 1;
  const ndcY = -((mouseY - rect.top) / rect.height) * 2 + 1;

  const [cx, cy, cz] = centroid;

  // Object→world matrix composes collection.transform ∘ obj local so the
  // centroid projects to the same NDC the user sees, and the unprojected
  // mouse point lands back in child-local vertex space.
  const w = worldTransformForSceneObject(obj, parentCollection);
  const objMatrix = new THREE.Matrix4().compose(
    new THREE.Vector3(w.position[0], w.position[1], w.position[2]),
    new THREE.Quaternion().setFromEuler(
      new THREE.Euler(w.rotation[0], w.rotation[1], w.rotation[2]),
    ),
    new THREE.Vector3(w.scale[0], w.scale[1], w.scale[2]),
  );
  const centroidWorld = new THREE.Vector3(cx, cy, cz).applyMatrix4(objMatrix);
  const centroidNDC = centroidWorld.clone().project(camera);

  const mouseWorld = new THREE.Vector3(ndcX, ndcY, centroidNDC.z).unproject(
    camera,
  );
  const invObj = objMatrix.clone().invert();
  const mouseLocal = mouseWorld.applyMatrix4(invObj);
  return [mouseLocal.x, mouseLocal.y, mouseLocal.z];
}

// ---------------------------------------------------------------------------
// Edit-mode raycast bridge (non-reactive, module-level)
// ---------------------------------------------------------------------------

/** Describes what the renderer hit-tested. */
export interface EditClickHit {
  /** The vertex indices at the click location. */
  vertices: number[];
  /** The closest edge on the hit polygon face, if determinable. */
  edge?: [number, number];
  /** The polygon face index that was hit. */
  faceIndex?: number;
}

/** Modifier key state at click time. */
export interface EditClickModifiers {
  shift: boolean;
  ctrl: boolean;
  alt: boolean;
}

/**
 * The renderer registers a raycast function that can resolve a screen-space
 * point to an EditClickHit.  The keyboard handler calls it when L is pressed.
 * This avoids relying on Three.js hover events which are unreliable for
 * invisible hit-test meshes.
 */
type EditRaycastFn = (clientX: number, clientY: number) => EditClickHit | null;
let _editRaycast: EditRaycastFn | null = null;
export function registerEditRaycast(fn: EditRaycastFn | null) {
  _editRaycast = fn;
}
export function raycastEdit(
  clientX: number,
  clientY: number,
): EditClickHit | null {
  return _editRaycast?.(clientX, clientY) ?? null;
}

// ---------------------------------------------------------------------------
// Connected component flood fill
// ---------------------------------------------------------------------------

/**
 * BFS flood fill from seed vertices through topological adjacency.
 * Returns the full connected component as a Set of vertex indices.
 */
export function floodFillConnected(
  seeds: number[],
  topology: PolygonTopology,
  vertices: Float32Array,
): Set<number> {
  const cache = getAdjacencyCache(topology, vertices.length / 3);
  const visited = new Set<number>();
  const queue: number[] = [];

  for (const v of seeds) {
    if (!visited.has(v)) {
      visited.add(v);
      queue.push(v);
    }
  }

  while (queue.length > 0) {
    const current = queue.pop()!;
    const neighbors = vertexNeighbors(cache, topology, current);
    for (const n of neighbors) {
      if (visited.has(n)) continue;
      visited.add(n);
      queue.push(n);
    }
  }

  return visited;
}
